import type { PickerOption } from "@/lib/liveavatar/options";

/** A missing catalogue entry is not proof that a persisted voice is invalid. */
export function liveAvatarVoiceOptions(
  options: PickerOption[],
  args: { importedVoicesOnly?: boolean; importedFirst?: boolean; selectedVoiceId?: string },
): PickerOption[] {
  let result = args.importedVoicesOnly
    ? options.filter((voice) => voice.source === "private")
    : [...options];
  if (args.importedFirst) {
    result = [
      ...result.filter((voice) => voice.source === "private"),
      ...result.filter((voice) => voice.source !== "private"),
    ];
  }
  const savedId = args.selectedVoiceId;
  if (savedId && !result.some((voice) => voice.id === savedId)) {
    result.unshift({ id: savedId, label: "Saved voice", sublabel: savedId });
  }
  return result;
}

export function shouldReconcileVoice(provider: string): boolean {
  return provider !== "liveavatar";
}
/** BYO LiveKit speaks through ElevenLabs, so only imported voices are safe there. */
export function isByoCompatibleVoice(options: PickerOption[], voiceId?: string): boolean {
  if (!voiceId) return false;
  return options.some((voice) => voice.id === voiceId && voice.source === "private");
}

export type VoiceFilters = { gender?: string; language?: string; accent?: string; search?: string };

const norm = (v?: string) => (v ?? "").trim().toLowerCase();

/** Distinct filter values present in the loaded voices; accents narrow to the chosen language. */
export function voiceFilterChoices(options: PickerOption[], args: { language?: string } = {}) {
  const uniq = (vals: (string | undefined)[]) =>
    [...new Set(vals.map(norm).filter(Boolean))].sort();
  const lang = norm(args.language);
  return {
    genders: uniq(options.map((o) => o.gender)),
    languages: uniq(options.map((o) => o.language)),
    accents: uniq(options.filter((o) => !lang || norm(o.language) === lang).map((o) => o.accent)),
  };
}

/** Filtering is display-only: the saved voice always stays in the list. */
export function filterVoices(
  options: PickerOption[],
  filters: VoiceFilters,
  savedVoiceId?: string,
): PickerOption[] {
  const matches = (value: string | undefined, wanted?: string) => !norm(wanted) || norm(value) === norm(wanted);
  return options.filter(
    (o) =>
      (savedVoiceId && o.id === savedVoiceId) ||
      (matches(o.gender, filters.gender) &&
        matches(o.language, filters.language) &&
        matches(o.accent, filters.accent) &&
        (!norm(filters.search) ||
          norm(`${o.label} ${o.sublabel ?? ""} ${o.accent ?? ""} ${o.language ?? ""}`).includes(norm(filters.search)))),
  );
}
